import { type FC, Suspense, useRef, useEffect, useContext } from 'react';
import classnames from 'classnames';
import { graphql, useFragment } from 'react-relay';

import { FormattedMessage } from 'dibs-react-intl';
import { Link } from 'dibs-elements/exports/Link.tsx';
import { GetHelpZendeskLatestOrder } from './GetHelpZendeskLatestOrder.tsx';
import { GetHelpZendeskLatestOrderSkeleton } from './GetHelpZendeskLatestOrderSkeleton.tsx';

import { GetHelpZendeskContext } from './GetHelpZendeskContext.ts';
import { trackGetHelpZendeskEvent } from './getHelpZendeskTracking.ts';

import { type GetHelpZendeskArticle_viewer$key } from './__generated__/GetHelpZendeskArticle_viewer.graphql.ts';

import dibsCss from 'dibs-css';
import styles from './GetHelpZendeskArticleStyles.scss';
import './GetHelpZendeskArticle.scss';

const GetHelpZendeskArticle: FC<{ viewer: GetHelpZendeskArticle_viewer$key }> = ({
    viewer: viewerRef,
}) => {
    const {
        state: { isCheckout, isSeller },
    } = useContext(GetHelpZendeskContext);
    const bodyRef = useRef<HTMLDivElement | null>(null);

    const viewer = useFragment(
        graphql`
            fragment GetHelpZendeskArticle_viewer on Viewer {
                zendeskArticles(isSeller: $isSeller, sellerId: $sellerId) {
                    article(articleId: $articleId) @include(if: $hasArticleId) {
                        body
                        title
                        url
                    }
                }
            }
        `,
        viewerRef
    );

    const { zendeskArticles } = viewer;
    const { body, title, url } = zendeskArticles?.article || {};

    useEffect(() => {
        trackGetHelpZendeskEvent({
            stepInteractionName: 'Support article viewed',
            trigger: title || '',
            isSeller,
        });
    }, [title, isSeller]);

    useEffect(() => {
        if (!isSeller) {
            return;
        }

        const imageTags = bodyRef.current?.getElementsByTagName('img') || [];

        [...imageTags].forEach(imageTag => {
            imageTag.src = `/dealers/contact/zendesk-photo?imageUrl=${imageTag.src}`;
            imageTag.classList.add(styles.shimmer);
            imageTag.onload = () => {
                imageTag.classList.remove(styles.shimmer);
            };
        });
    }, [isSeller]);

    return (
        <div className={dibsCss.mbMedium}>
            <div className="zendesk-article-styles">
                <div className={classnames(dibsCss.sassyFontHeaderLarge, dibsCss.mbMedium)}>
                    {title}
                </div>
                <div
                    ref={bodyRef}
                    className={classnames('content', dibsCss.mbSmall)}
                    dangerouslySetInnerHTML={{
                        __html: body || '',
                    }}
                />
                <Link
                    href={url || ''}
                    target="_blank"
                    onClick={() => {
                        trackGetHelpZendeskEvent({
                            stepInteractionName: 'Navigate to support center',
                            isSeller,
                        });
                    }}
                >
                    <FormattedMessage
                        id="getHelp.GetHelpZendeskArticle.viewSupportCenterLink"
                        defaultMessage="View in Support Center"
                    />
                </Link>
            </div>
            <Suspense fallback={<GetHelpZendeskLatestOrderSkeleton />}>
                {!isCheckout && <GetHelpZendeskLatestOrder />}
            </Suspense>
        </div>
    );
};

export { GetHelpZendeskArticle };
